import { useEffect, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { ActionButton } from '../ui/ActionButton.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { api, type TenantUser } from '../net/apiClient.js'
import { fehlerText } from '../net/errors.js'
import { uuidv7 } from '../net/ids.js'

/**
 * Firma und Person – nebeneinander, damit der Unterschied nicht erklärt
 * werden muss.
 *
 * Die beiden Ebenen hängen zusammen und sind trotzdem verschieden. Ein
 * Betrieb hat eine Anschrift, eine Nummer und eine Mailadresse; sie stehen
 * auf jeder Rechnung und jeder Offerte und bleiben stehen, auch wenn eine
 * Person geht. Jede Person hat eine eigene Mailadresse und eine eigene
 * Nummer; sie stehen auf keinem Beleg und gehen mit ihr.
 *
 * Im Datenmodell war das von Anfang an getrennt – tenant_settings hier,
 * users dort. Was fehlte, war der Bildschirm, auf dem man beides sieht. Und
 * solange den niemand hat, ist die Trennung für den Benutzer keine: er sieht
 * ein Feld «Telefon» in den Einstellungen, tippt seine Handynummer hinein
 * und merkt ein halbes Jahr später, dass Kunden ihn privat anrufen.
 *
 * Deshalb steht oben eine Gegenüberstellung und keine Liste. Links, was auf
 * den Beleg geht; rechts, was nicht. Darunter erst die Felder zum Ausfüllen.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was hier bewusst nicht geht
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Die Anmeldeadresse ist nur zu lesen. An ihr hängen die Anmeldung und der
 * zweite Faktor; ein Feld, in dem sie sich nebenbei tauschen liesse, wäre der
 * Weg, auf dem jemand ein übernommenes Konto dauerhaft behält.
 *
 * Die Rolle ebenfalls. Aus einem Monteur einen Inhaber zu machen heisst, ihm
 * sämtliche Preise, Umsätze und die IBAN zu öffnen – das gehört nicht neben
 * ein Telefonfeld.
 */

export interface Firmenangaben {
  name: string
  street: string
  zip: string
  city: string
  uid: string
  phone: string
  email: string
}

function rollenwort(rolle: TenantUser['role']): string {
  return rolle === 'superuser' ? 'Büro / Inhaber' : 'Monteur'
}

/** Eine Zeile in einer der beiden Spalten. Fehlt der Wert, sagt sie das. */
function Angabe({ was, wert }: { was: string; wert: string }): JSX.Element {
  return (
    <div className="o-angabe">
      <span>{was}</span>
      <strong className={wert ? undefined : 'fehlt'}>{wert || 'noch nicht eingetragen'}</strong>
    </div>
  )
}

export function Team({
  session,
  firma
}: {
  session: SessionUser
  /** Die offiziellen Angaben des Betriebs – hier nur zum Vergleich, zum Lesen. */
  firma: Firmenangaben
}): JSX.Element {
  const [leute, setLeute] = useState<TenantUser[]>([])
  const [geladen, setGeladen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  /* Was im Feld steht, pro Person – getrennt von dem, was der Server kennt. */
  const [entwurf, setEntwurf] = useState<Record<string, { name: string; phone: string }>>({})
  const [neu, setNeu] = useState<{
    name: string
    email: string
    phone: string
    role: 'superuser' | 'worker'
  }>({ name: '', email: '', phone: '', role: 'worker' })
  /* Der Link, solange kein echter Mailanbieter angeschlossen ist. */
  const [offenerLink, setOffenerLink] = useState<string | null>(null)
  /*
    Ein Dialog für alle folgenreichen Handlungen.

    Rolle ändern, einen Inhaber stilllegen, einen Rücksetzlink auslösen, einen
    zweiten Faktor abräumen – vier verschiedene Knöpfe, aber derselbe Vorgang:
    «bitte weise dich noch einmal aus». Vier eigene Dialoge wären vier Stellen,
    an denen das Codefeld vergessen wird.
  */
  const [nachweis, setNachweis] = useState<{
    aktion: 'rolle' | 'deaktivieren' | 'entfernen' | 'passwortlink' | 'zweiterfaktor' | 'einladen'
    user: TenantUser | null
    frage: string
  } | null>(null)
  const [nwPasswort, setNwPasswort] = useState('')
  const [nwCode, setNwCode] = useState('')
  const [nwGrund, setNwGrund] = useState('')

  useEffect(() => {
    void api
      .listUsers()
      .then((r) => {
        const us = r.users as TenantUser[]
        setLeute(us)
        setEntwurf(
          Object.fromEntries(us.map((u) => [u.userId, { name: u.name, phone: u.phone ?? '' }]))
        )
      })
      .catch((e) => setFehler(fehlerText(e)))
      .finally(() => setGeladen(true))
  }, [])

  function setzen(userId: string, feld: 'name' | 'phone', wert: string): void {
    setEntwurf((v) => ({ ...v, [userId]: { ...v[userId], [feld]: wert } }))
  }

  async function speichern(u: TenantUser): Promise<void> {
    const e = entwurf[u.userId]
    if (!e || !e.name.trim()) {
      notify.warn('Ohne Namen geht es nicht.')
      return
    }
    try {
      const antwort = await api.teamAngabenSetzen(
        u.userId,
        { name: e.name.trim(), phone: e.phone.trim() || null },
        uuidv7()
      )
      const neu = antwort.user
      if (neu) setLeute((liste) => liste.map((x) => (x.userId === neu.userId ? neu : x)))
      notify.ok(`${e.name.trim()} gespeichert.`)
    } catch (err) {
      notifyError(err)
    }
  }

  /** Die Liste neu holen. Nach jeder Änderung, damit nichts geraten wird. */
  async function neuLaden(): Promise<void> {
    const r = await api.listUsers()
    const us = r.users as TenantUser[]
    setLeute(us)
    setEntwurf(Object.fromEntries(us.map((u) => [u.userId, { name: u.name, phone: u.phone ?? '' }])))
  }

  /**
   * Was mit einem verschickten Link zu tun ist.
   *
   * Solange kein echter Mailanbieter angeschlossen ist, kommt der Link in der
   * Antwort mit und wird hier angezeigt – sonst käme niemand hinein, und der
   * Betrieb hielte die Einladung für kaputt. Sobald wirklich zugestellt wird,
   * ist das Feld leer und die Meldung sagt schlicht, dass die Mail unterwegs
   * ist. Eine Zustellung vorzutäuschen wäre die gefährlichste Variante.
   */
  function meldeLink(a: { link: string | null; zustellungEcht: boolean; versandFehler: string | null }, wem: string): void {
    if (a.versandFehler && !a.link) {
      notify.warn(`${a.versandFehler} Bitte später nochmals versuchen.`)
      return
    }
    if (a.zustellungEcht) {
      notify.ok(`E-Mail an ${wem} ist unterwegs.`)
      return
    }
    setOffenerLink(a.link)
    notify.warn('Der Versand läuft noch über den Entwicklungs-Ausgang. Link unten weitergeben.')
  }

  /*
    Die folgenreichen Handlungen laufen alle über denselben Dialog.

    Der Knopf stellt nur die Frage; ausgeführt wird erst, wenn der Nachweis
    erbracht ist. `window.confirm` reicht dafür nicht – eine Rückfrage, die man
    mit der Eingabetaste wegdrückt, ist kein Nachweis.
  */
  function frage(
    aktion: 'rolle' | 'deaktivieren' | 'entfernen' | 'passwortlink' | 'zweiterfaktor' | 'einladen',
    user: TenantUser | null,
    text: string
  ): void {
    setNwPasswort('')
    setNwCode('')
    setNwGrund('')
    setNachweis({ aktion, user, frage: text })
  }

  function schliesseDialog(): void {
    setNachweis(null)
    setNwPasswort('')
    setNwCode('')
    setNwGrund('')
  }

  /** Nur wenn die angemeldete Person selbst einen zweiten Faktor hat. */
  const brauchtCode = leute.find((u) => u.userId === session.userId)?.zweiterFaktor ?? false

  async function einladenAusfuehren(mitNachweis: boolean): Promise<void> {
    const a = await api.benutzerEinladen(
      {
        name: neu.name.trim(),
        email: neu.email.trim(),
        role: neu.role,
        phone: neu.phone.trim() || undefined,
        ...(mitNachweis ? { passwort: nwPasswort, code: nwCode || undefined } : {})
      },
      uuidv7()
    )
    meldeLink(a, neu.email.trim())
    setNeu({ name: '', email: '', phone: '', role: 'worker' })
    await neuLaden()
  }

  async function einladen(): Promise<void> {
    if (!neu.name.trim() || !neu.email.trim()) {
      notify.warn('Name und E-Mail-Adresse braucht es.')
      return
    }
    /* Ein Monteur ohne Rückfrage; volle Rechte nie ohne Nachweis. */
    if (neu.role === 'superuser') {
      frage(
        'einladen',
        null,
        `${neu.name.trim()} bekommt volle Rechte: alle Preise, alle Umsätze, die Bankverbindung und die Benutzerverwaltung.`
      )
      return
    }
    await einladenAusfuehren(false)
  }

  function passwortLink(u: TenantUser): void {
    frage(
      'passwortlink',
      u,
      `${u.name} bekommt einen Link, mit dem ein neues Passwort gesetzt wird. Der bisherige Zugang bleibt bis dahin gültig.`
    )
  }

  function rolleWechseln(u: TenantUser): void {
    frage(
      'rolle',
      u,
      u.role === 'worker'
        ? `${u.name} bekommt volle Rechte: alle Preise, alle Umsätze, die Bankverbindung und die Benutzerverwaltung.`
        : `${u.name} verliert die vollen Rechte und sieht danach nur noch die eigenen Rapporte. Die laufenden Anmeldungen werden beendet.`
    )
  }

  function deaktivieren(u: TenantUser): void {
    frage(
      'deaktivieren',
      u,
      `${u.name} kann sich danach nicht mehr anmelden. Die bisherigen Rapporte und Unterschriften bleiben erhalten.`
    )
  }

  /**
   * Aus dem Betrieb entfernen – oder eine Einladung zurückziehen.
   *
   * Zwei Sätze für dieselbe Handlung, weil es für den Betrieb zwei
   * verschiedene Dinge sind: Eine Einladung, die nie angenommen wurde,
   * nimmt man zurück; eine Person, die hier gearbeitet hat, entfernt man –
   * und ihre Rapporte und Unterschriften bleiben, wo sie sind. Genau das
   * muss im Satz stehen, sonst klickt jemand in der Annahme, es werde
   * aufgeräumt.
   */
  function entfernen(u: TenantUser): void {
    frage(
      'entfernen',
      u,
      u.einladungOffen
        ? `Die Einladung an ${u.name} wird zurückgezogen. Der Link wird ungültig, ` +
            'und es entsteht kein Zugang zu deinem Betrieb. Eine neue Einladung ist ' +
            'jederzeit möglich.'
        : `${u.name} wird dauerhaft aus deinem Betrieb entfernt: Mitgliedschaft, ` +
            'Baustellenzuteilungen, angemeldete Geräte und laufende Anmeldungen. ' +
            'Rapporte, Unterschriften, Rechnungen und Dokumente bleiben unverändert. ' +
            'Zurückkommen kann die Person nur über eine neue Einladung.'
    )
  }

  async function reaktivieren(u: TenantUser): Promise<void> {
    await api.benutzerReaktivieren(u.userId, uuidv7())
    notify.ok(`${u.name} ist wieder aufgenommen.`)
    await neuLaden()
  }

  /** Der eine Ausführungsweg, hinter dem Nachweis. */
  async function nachweisAusfuehren(): Promise<void> {
    if (!nachweis) return
    const nw = { passwort: nwPasswort, code: nwCode.trim() || undefined }
    const u = nachweis.user

    if (nachweis.aktion === 'einladen') {
      await einladenAusfuehren(true)
    } else if (u && nachweis.aktion === 'rolle') {
      await api.rolleSetzen(u.userId, u.role === 'worker' ? 'superuser' : 'worker', nw, uuidv7())
      notify.ok(`${u.name}: Rolle geändert.`)
    } else if (u && nachweis.aktion === 'deaktivieren') {
      await api.benutzerDeaktivieren(u.userId, nw, uuidv7())
      notify.ok(`Zugang von ${u.name} geschlossen.`)
    } else if (u && nachweis.aktion === 'entfernen') {
      const r = await api.benutzerEntfernen(u.userId, nw, uuidv7())
      notify.ok(
        r.art === 'einladung'
          ? `Die Einladung an ${u.name} ist zurückgezogen.`
          : `${u.name} ist aus dem Betrieb entfernt.`
      )
    } else if (u && nachweis.aktion === 'passwortlink') {
      meldeLink(await api.passwortLinkSenden(u.userId, nw, uuidv7()), u.email)
    } else if (u && nachweis.aktion === 'zweiterfaktor') {
      await api.zweitenFaktorZuruecksetzen(u.userId, nw, nwGrund.trim(), uuidv7())
      notify.ok(`Zweiter Faktor von ${u.name} zurückgesetzt.`)
    }

    schliesseDialog()
    await neuLaden()
  }

  /*
    Rechts steht die angemeldete Person, nicht «irgendeine».

    Die Gegenüberstellung soll den eigenen Fall zeigen: das hier ist deine
    Nummer, das daneben ist die der Firma. Ein fremdes Beispiel würde die
    Frage offen lassen, in welcher Spalte man selber steht.
  */
  const ich = leute.find((u) => u.userId === session.userId) ?? null
  const ort = [firma.zip, firma.city].filter(Boolean).join(' ')

  return (
    <div id="abschnitt-team">
      <div className="o-gegenueber" id="team-gegenueber">
        <div className="o-seite gehoert-firma" id="team-seite-firma">
          <div className="o-seite-kopf">
            <b>Firma</b>
            <strong>{firma.name || 'Noch ohne Namen'}</strong>
            <i>Steht auf jeder Rechnung und jeder Offerte</i>
          </div>
          <div className="o-seite-leib">
            <Angabe was="Adresse" wert={[firma.street, ort].filter(Boolean).join(', ')} />
            <Angabe was="UID / MWST-Nummer" wert={firma.uid} />
            <Angabe was="Telefon, offiziell" wert={firma.phone} />
            <Angabe was="E-Mail, offiziell" wert={firma.email} />
          </div>
          <div className="o-seite-fuss">
            Gehört dem Betrieb. Bleibt gleich, wenn jemand kommt oder geht. Zu ändern unter{' '}
            <strong>Firma</strong>.
          </div>
        </div>

        <div className="o-seite gehoert-person" id="team-seite-person">
          <div className="o-seite-kopf">
            <b>Person</b>
            <strong>{ich?.name || session.name || 'Du'}</strong>
            <i>Steht auf keinem Beleg</i>
          </div>
          <div className="o-seite-leib">
            <Angabe was="Rolle" wert={ich ? rollenwort(ich.role) : ''} />
            <Angabe was="Anmeldeadresse" wert={ich?.email ?? session.email ?? ''} />
            <Angabe was="Eigene Telefonnummer" wert={ich?.phone ?? ''} />
            <Angabe was="Zweiter Faktor" wert="hängt an der Anmeldeadresse" />
          </div>
          <div className="o-seite-fuss">
            Gehört der Person. Geht mit ihr. Unten zu ändern — ausser der Anmeldeadresse.
          </div>
        </div>
      </div>

      {/*
        Der Nachweis, für alle folgenreichen Handlungen derselbe.

        Er steht oben und nicht neben dem Knopf: was hier bestätigt wird, ist
        eine Änderung an der Machtverteilung im Betrieb, und die soll man
        lesen, bevor man tippt.
      */}
      {nachweis && (
        <div className="o-card alarm" id="team-nachweis" style={{ marginTop: 16 }}>
          <div className="o-card-head">
            <h3>Bitte bestätigen</h3>
          </div>
          <div className="o-pad">
            <p style={{ marginTop: 0 }}>{nachweis.frage}</p>

            <div className="o-grid o-grid-3">
              <div className="o-field">
                <label htmlFor="nw-passwort">Dein eigenes Passwort</label>
                <input
                  id="nw-passwort"
                  type="password"
                  autoComplete="current-password"
                  value={nwPasswort}
                  onChange={(e) => setNwPasswort(e.target.value)}
                />
              </div>

              {brauchtCode && (
                <div className="o-field">
                  <label htmlFor="nw-code">Code aus deiner App</label>
                  <input
                    id="nw-code"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    value={nwCode}
                    onChange={(e) => setNwCode(e.target.value)}
                  />
                  <span className="hint">Du hast einen zweiten Faktor – hier zählt er.</span>
                </div>
              )}

              {nachweis.aktion === 'zweiterfaktor' && (
                <div className="o-field">
                  <label htmlFor="nw-grund">Grund</label>
                  <input
                    id="nw-grund"
                    autoComplete="off"
                    placeholder="Telefon verloren"
                    value={nwGrund}
                    onChange={(e) => setNwGrund(e.target.value)}
                  />
                  <span className="hint">Steht im Protokoll des Betriebs.</span>
                </div>
              )}
            </div>

            <div className="o-aktionen" style={{ marginTop: 12 }}>
              <ActionButton
                id="btn-nachweis-ausfuehren"
                disabled={
                  nwPasswort.length === 0 ||
                  (brauchtCode && nwCode.trim().length < 6) ||
                  (nachweis.aktion === 'zweiterfaktor' && nwGrund.trim().length < 5)
                }
                labels={{ idle: 'Bestätigen', busy: 'Führt aus …', success: 'Erledigt' }}
                onAction={nachweisAusfuehren}
              />
              <button
                type="button"
                className="o-btn"
                id="btn-nachweis-abbrechen"
                onClick={schliesseDialog}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="o-card" style={{ marginTop: 16 }}>
        <div className="o-card-head">
          <h3>Die Personen im Betrieb</h3>
          <span className="o-soft">Name und Nummer, je Person</span>
        </div>
        <div className="o-pad">
          <p className="o-soft" style={{ marginTop: 0 }}>
            Jede Person hat eine eigene Anmeldeadresse. Teilen sich mehrere Leute ein Postfach, hat
            jeder, der es öffnen kann, den zweiten Faktor von allen — die Anmeldung ist dann wieder
            einfaktorig, nur mit mehr Schritten.
          </p>

          {fehler && (
            <div className="o-band alarm" id="team-fehler">
              <span>{fehler}</span>
            </div>
          )}

          {!geladen && <p className="o-soft">Wird geladen …</p>}

          {geladen && leute.length === 0 && !fehler && (
            <p className="o-leer">Niemand ausser dir. Unten lädst du jemanden ein.</p>
          )}

          {/*
            Jemanden einladen.

            Kein Passwortfeld – bewusst. Der neue Zugang bekommt eine
            Zufallszeichenfolge, die niemand kennt, und die eingeladene Person
            setzt ihr eigenes Passwort über den Link in ihrer Mail. Ein
            Passwort, das der Chef vergibt, kennt der Chef; eine Unterschrift
            unter einem Rapport ist dann nicht mehr die des Monteurs.
          */}
          <div className="o-kasten" id="team-einladen" style={{ marginTop: 16 }}>
            <div className="o-kasten-kopf">
              <b>Jemanden einladen</b>
              <i>Die Person setzt ihr Passwort selbst</i>
            </div>
            <div className="o-grid o-grid-4">
              <div className="o-field">
                <label htmlFor="neu-name">Name</label>
                <input
                  id="neu-name"
                  autoComplete="off"
                  value={neu.name}
                  onChange={(e) => setNeu({ ...neu, name: e.target.value })}
                />
              </div>
              <div className="o-field">
                <label htmlFor="neu-mail">E-Mail-Adresse</label>
                <input
                  id="neu-mail"
                  autoComplete="off"
                  inputMode="email"
                  value={neu.email}
                  onChange={(e) => setNeu({ ...neu, email: e.target.value })}
                />
                <span className="hint">Die eigene Adresse der Person, kein geteiltes Postfach.</span>
              </div>
              <div className="o-field">
                <label htmlFor="neu-phone">Telefon</label>
                <input
                  id="neu-phone"
                  autoComplete="off"
                  inputMode="tel"
                  placeholder="+41 …"
                  value={neu.phone}
                  onChange={(e) => setNeu({ ...neu, phone: e.target.value })}
                />
              </div>
              <div className="o-field">
                <label htmlFor="neu-rolle">Rolle</label>
                <select
                  id="neu-rolle"
                  value={neu.role}
                  onChange={(e) =>
                    setNeu({ ...neu, role: e.target.value as 'superuser' | 'worker' })
                  }
                >
                  <option value="worker">Monteur</option>
                  <option value="superuser">Büro / Inhaber</option>
                </select>
                <span className="hint">
                  {neu.role === 'worker'
                    ? 'Sieht nur die eigenen Rapporte.'
                    : 'Sieht alles, auch Preise, Umsätze und die Bankverbindung.'}
                </span>
              </div>
            </div>
            <div className="o-aktionen" style={{ marginTop: 12 }}>
              <ActionButton
                id="btn-team-einladen"
                disabled={!neu.name.trim() || !neu.email.trim()}
                labels={{ idle: 'Einladen', busy: 'Lädt ein …', success: 'Eingeladen' }}
                onAction={einladen}
              />
            </div>

            {offenerLink && (
              <div className="o-band" id="team-offener-link" style={{ marginTop: 12 }}>
                <span>
                  Der E-Mail-Versand ist noch nicht aufgeschaltet. Gib diesen Link persönlich
                  weiter — er gilt einmal und läuft ab:
                  <br />
                  <code>{offenerLink}</code>
                </span>
              </div>
            )}
          </div>

          {leute.map((u) => {
            const e = entwurf[u.userId] ?? { name: u.name, phone: u.phone ?? '' }
            const geaendert = e.name !== u.name || e.phone !== (u.phone ?? '')
            return (
              <div
                className="o-kasten"
                key={u.userId}
                id={`team-${u.userId}`}
                style={{ marginTop: 12 }}
              >
                <div className="o-kasten-kopf">
                  <b>
                    {u.name}
                    {u.userId === session.userId && <span className="o-soft"> — du</span>}
                  </b>
                  <i>
                    {rollenwort(u.role)}
                    {u.deaktiviert && ' · Zugang geschlossen'}
                    {u.einladungOffen && ' · Einladung offen'}
                  </i>
                </div>

                <div className="o-grid o-grid-3">
                  <div className="o-field">
                    <label htmlFor={`team-name-${u.userId}`}>Name</label>
                    <input
                      autoComplete="off"
                      id={`team-name-${u.userId}`}
                      value={e.name}
                      onChange={(ev) => setzen(u.userId, 'name', ev.target.value)}
                    />
                  </div>
                  <div className="o-field">
                    <label htmlFor={`team-phone-${u.userId}`}>Eigene Telefonnummer</label>
                    <input
                      autoComplete="off"
                      id={`team-phone-${u.userId}`}
                      inputMode="tel"
                      value={e.phone}
                      placeholder="+41 …"
                      onChange={(ev) => setzen(u.userId, 'phone', ev.target.value)}
                    />
                    <span className="hint">Nicht die Nummer der Firma.</span>
                  </div>
                  <div className="o-field">
                    <label htmlFor={`team-mail-${u.userId}`}>Anmeldeadresse</label>
                    <input
                      autoComplete="off"
                      id={`team-mail-${u.userId}`}
                      value={u.email}
                      readOnly
                      disabled
                    />
                    <span className="hint">Trägt den zweiten Faktor. Hier nicht änderbar.</span>
                  </div>
                </div>

                <div className="o-aktionen" style={{ marginTop: 12 }}>
                  <ActionButton
                    id={`btn-team-speichern-${u.userId}`}
                    disabled={!geaendert}
                    labels={{ idle: 'Speichern', busy: 'Speichert …', success: 'Gespeichert' }}
                    onAction={() => speichern(u)}
                  />

                  <button
                    type="button"
                    className="o-btn"
                    id={`btn-team-link-${u.userId}`}
                    disabled={u.deaktiviert}
                    onClick={() => passwortLink(u)}
                  >
                    Passwort zurücksetzen
                  </button>

                  {/*
                    Die Rolle. Ein eigener Knopf mit Nachweis und nicht ein
                    Auswahlfeld: aus einem Monteur einen Inhaber zu machen
                    öffnet sämtliche Preise, Umsätze und die Bankverbindung.
                    Das ist keine Einstellung, das ist eine Entscheidung.
                  */}
                  <button
                    type="button"
                    className="o-btn"
                    id={`btn-team-rolle-${u.userId}`}
                    disabled={u.deaktiviert}
                    onClick={() => rolleWechseln(u)}
                  >
                    {u.role === 'worker' ? 'Volle Rechte geben' : 'Zu Monteur machen'}
                  </button>

                  {u.zweiterFaktor && (
                    <button
                      type="button"
                      className="o-btn"
                      id={`btn-team-2fa-${u.userId}`}
                      onClick={() =>
                        frage(
                          'zweiterfaktor',
                          u,
                          `Danach meldet sich ${u.name} nur noch mit Passwort an und richtet den zweiten Faktor neu ein. Alle laufenden Anmeldungen dieser Person werden beendet.`
                        )
                      }
                    >
                      Zweiten Faktor zurücksetzen
                    </button>
                  )}

                  {u.deaktiviert ? (
                    <ActionButton
                      id={`btn-team-reaktivieren-${u.userId}`}
                      labels={{ idle: 'Wieder aufnehmen', busy: '…', success: 'Aufgenommen' }}
                      onAction={() => reaktivieren(u)}
                    />
                  ) : (
                    <button
                      type="button"
                      className="o-btn"
                      id={`btn-team-deaktivieren-${u.userId}`}
                      onClick={() => deaktivieren(u)}
                    >
                      Zugang schliessen
                    </button>
                  )}

                  {/*
                    Entfernen steht NEBEN dem Stilllegen und nicht an seiner
                    Stelle: Das eine ist eine Pause, das andere ein Ende.
                    Beides zu haben ist der ganze Punkt – wer nur entfernen
                    kann, legt niemanden mehr für die Zwischensaison still.

                    Bei der eigenen Zeile fehlt der Knopf. Der Server weist
                    es ohnehin ab; hier ist es die Höflichkeit, einen Klick
                    nicht anzubieten, der nicht geht.
                  */}
                  {u.userId !== session.userId && (
                    <button
                      type="button"
                      className="o-btn o-btn-ghost"
                      id={`btn-team-entfernen-${u.userId}`}
                      onClick={() => entfernen(u)}
                    >
                      {u.einladungOffen ? 'Einladung zurückziehen' : 'Aus Betrieb entfernen'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
